import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BookOpen, Plus, Search } from "lucide-react";

import { PageContent } from "@/shared/widgets/layout/PageContent";
import { Pagination } from "@/shared/widgets/pagination";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useTranslation } from "@/shared/i18n";

import { TemplateListCard } from "../components/TemplateListCard";
import { useTemplates, useDeleteTemplate } from "../model";
import type { GetTemplatesParams, TemplateStatus } from "../api";

const STATUS_OPTIONS: Array<{ value: "ALL" | TemplateStatus; labelKey: string }> = [
  { value: "ALL", labelKey: "status.all" },
  { value: "DRAFT", labelKey: "status.DRAFT" },
  { value: "READY", labelKey: "status.READY" },
  { value: "ARCHIVED", labelKey: "status.ARCHIVED" },
];

export default function TemplatesListPage() {
  const { t } = useTranslation("psdTemplates");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get("name") ?? "");
  const [status, setStatus] = useState<"ALL" | TemplateStatus>(
    (searchParams.get("status") as TemplateStatus | null) ?? "ALL",
  );
  const [page, setPage] = useState(Number(searchParams.get("page") ?? "1") || 1);
  const [limit, setLimit] = useState(Number(searchParams.get("limit") ?? "24") || 24);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  const filters: GetTemplatesParams = useMemo(() => {
    const f: GetTemplatesParams = { page, limit };
    if (debouncedSearch.trim()) f.name = debouncedSearch.trim();
    if (status !== "ALL") f.status = status;
    return f;
  }, [debouncedSearch, status, page, limit]);

  const { templates, totalPages, isLoading } = useTemplates(filters);
  const { deleteTemplate, isLoading: deleting } = useDeleteTemplate();

  const updateSearchParams = useCallback(
    (next: { page?: number; limit?: number; name?: string; status?: string }) => {
      const params = new URLSearchParams(searchParams);
      const set = (k: string, v: string | number | undefined) => {
        if (v === undefined || v === "" || v === "ALL") params.delete(k);
        else params.set(k, String(v));
      };
      if ("page" in next) set("page", next.page);
      if ("limit" in next) set("limit", next.limit);
      if ("name" in next) set("name", next.name);
      if ("status" in next) set("status", next.status);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handlePageChange = (next: number) => {
    setPage(next);
    updateSearchParams({ page: next });
  };

  const handlePageSizeChange = (next: number) => {
    setLimit(next);
    setPage(1);
    updateSearchParams({ limit: next, page: 1 });
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
    updateSearchParams({ name: value, page: 1 });
  };

  const handleStatusChange = (value: string) => {
    setStatus(value as "ALL" | TemplateStatus);
    setPage(1);
    updateSearchParams({ status: value, page: 1 });
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      await deleteTemplate(pendingDeleteId);
    } finally {
      setPendingDeleteId(null);
    }
  };

  return (
    <PageContent
      title={t("pageTitle")}
      items={[{ label: t("pageTitle"), href: "/psd-templates" }]}
      actions={
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <BookOpen className="w-4 h-4 mr-2" />
                {t("tutorials.button")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => window.open("/tutorials/add-template", "_blank")}
              >
                {t("tutorials.addTemplate")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => window.open("/tutorials/psd-guide", "_blank")}
              >
                {t("tutorials.templateGuide")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => navigate("/psd-templates/new")}>
            <Plus className="w-4 h-4 mr-2" />
            {t("list.newTemplate")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t("list.searchPlaceholder")}
              className="pl-9"
            />
          </div>
          <Select value={status} onValueChange={handleStatusChange}>
            <SelectTrigger className="sm:w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-2">
            {Array.from({ length: limit }).map((_, i) => (
              <div key={i} className="aspect-[3/4] rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
        ) : templates.length === 0 ? (
          <div className="border border-dashed rounded-xl p-12 text-center text-muted-foreground">
            <p>{t("list.empty")}</p>
            <Button className="mt-4" onClick={() => navigate("/psd-templates/new")}>
              <Plus className="w-4 h-4 mr-2" />
              {t("list.newTemplate")}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-2">
            {templates.map((item) => (
              <TemplateListCard
                key={item.id}
                item={item}
                onDelete={(id) => setPendingDeleteId(id)}
                disabled={deleting}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            setCurrentPage={handlePageChange}
            pageSize={limit}
            onPageSizeChange={handlePageSizeChange}
            showPageSizeOptions
            pageSizeOptions={[24, 48, 96]}
            showTotalItems={false}
          />
        )}
      </div>

      <AlertDialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => !open && setPendingDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("list.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("list.deleteDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("list.deleteCancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} disabled={deleting}>
              {t("list.deleteConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContent>
  );
}
